import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigation, Scrollbar, Mousewheel } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import { ProductCard } from "@app/scripts/react-product/components/ProductCard";

import type { NormalisedProduct } from "@app/scripts/types";

type Props = {
  disabledMobile: boolean;
  disabledDesktop: boolean;
  columnsDesktop: number | "auto";
  columnsMobile: number | "auto";
  products?: NormalisedProduct[];
  hasQuickAddDrawer?: boolean;
  quickAddStyle?: "default" | "pin" | "plp";
  spaceBetween?: number;
  showArrows?: boolean;
  showScrollbar?: boolean;
  addToCartBtns?: boolean;
  addAsGift?: boolean;
  arrowsMobile?: boolean;
  gifts?: NormalisedProduct[]
};

export const ProductCarousel: React.FC<Props> = ({
  disabledMobile,
  disabledDesktop,
  columnsDesktop,
  columnsMobile,
  products,
  hasQuickAddDrawer,
  quickAddStyle,
  spaceBetween = 8,
  showArrows = false,
  showScrollbar = false,
  addToCartBtns = false,
  addAsGift = false,
  arrowsMobile = false,
  gifts
}) => {
  const breakpoint = 990;
  const id = `item-carousel-${Math.floor(Math.random() * 1000000000)}`;

  const [isSlider, setIsSlider] = useState(false);

  const swiperRef = useRef(null);

  const setBtnTop = () => {
    setTimeout(() => {
      const imageHeight = swiperRef.current?.querySelector(".carousel-item img")?.getBoundingClientRect()?.height;
      const next = swiperRef.current?.querySelector(".next-btn");
      const prev = swiperRef.current?.querySelector(".prev-btn");

      if (imageHeight && next && prev) {
        next.style.top = `${Math.floor(imageHeight / 2)}px`;
        prev.style.top = `${Math.floor(imageHeight / 2)}px`;
      }
    }, 100);
  };

  const handleWindowDimensionChange = useCallback(() => {
    setIsSlider(
      (window.innerWidth >= breakpoint && !disabledDesktop) || (window.innerWidth < breakpoint && !disabledMobile)
    );
    setBtnTop();
  }, [breakpoint, disabledMobile, disabledDesktop]);

  useEffect(() => {
    setBtnTop();
  }, []);

  useEffect(() => {
    setBtnTop();
  }, [products]);

  useEffect(() => {
    handleWindowDimensionChange();
    window.addEventListener("resize", handleWindowDimensionChange);
    return () => window.removeEventListener("resize", handleWindowDimensionChange);
  }, []);

  return (
    <Swiper
      id={id}
      ref={swiperRef}
      className="product-grid item-carousel"
      loop={false}
      modules={[Navigation, Scrollbar, Mousewheel]}
      resistanceRatio={0.5}
      speed={400}
      navigation={{
        nextEl: `#${id} .next-btn`,
        prevEl: `#${id} .prev-btn`,
      }}
      scrollbar={{
        el: `#${id} .swiper-scrollbar`,
        draggable: true,
      }}
      mousewheel={{
        forceToAxis: true,
        thresholdDelta: 10,
      }}
      spaceBetween={spaceBetween}
      slidesPerView={columnsMobile}
      breakpoints={{
        [breakpoint]: {
          slidesPerView: columnsDesktop,
        },
      }}
    >
      {products?.map((product) => (
        <SwiperSlide>
          <ProductCard
            product={product}
            additionalClass={"carousel-item"}
            hasQuickAddDrawer={hasQuickAddDrawer}
            quickAddStyle={quickAddStyle}
            addToCartBtn={addToCartBtns}
            addAsGift={addAsGift}
            gifts={gifts}
          />
        </SwiperSlide>
      ))}

      {showArrows ? (
        <>
          <button
            type="button"
            aria-label="Next products"
            className={`button next-btn button--carousel button--white ${!arrowsMobile ? "hide-mobile" : ""}`}
          >
            <span>
              <svg>
                <use xlinkHref="#icon-chevron-right-small"></use>
              </svg>
            </span>
          </button>

          <button
            type="button"
            aria-label="Previous products"
            className={`button prev-btn button--carousel button--white ${!arrowsMobile ? "hide-mobile" : ""}`}
          >
            <span>
              <svg>
                <use xlinkHref="#icon-chevron-left-small"></use>
              </svg>
            </span>
          </button>
        </>
      ) : (
        ""
      )}

      {showScrollbar ? <div className="swiper-scrollbar hide-mobile swiper-scrollbar-horizontal"></div> : ""}
    </Swiper>
  );
};
